import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Leaf, MapPin, Clock, Calendar, Users, ArrowRight, ShieldCheck, Lock, LogIn } from "lucide-react";
import { toast } from "sonner";

export default function PublicVacancy() {
  const { shareCode } = useParams();
  const [v, setV] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Wait until AuthContext resolves
    if (authLoading) return;
    // If not logged in, redirect to login with returnTo
    if (!user) {
      navigate(`/login?returnTo=/v/${shareCode}`, { replace: true });
      return;
    }
    const src = new URLSearchParams(window.location.search).get("src") || "share";
    api.get(`/public/vacancies/${shareCode}?source=${src}`)
      .then(({ data }) => setV(data))
      .catch((err) => {
        const detail = err.response?.data?.detail;
        setError(detail || "Erro ao carregar vaga");
        // Route users to onboarding if needed
        if (err.response?.status === 403) {
          const msg = String(detail || "");
          if (msg.toLowerCase().includes("perfil")) {
            toast.info("Complete o seu perfil primeiro.");
            navigate(`/onboarding/profile?returnTo=/v/${shareCode}`, { replace: true });
          } else if (msg.toLowerCase().includes("protocolo")) {
            toast.info("Assine o protocolo para aceder às vagas.");
            navigate(`/onboarding/protocol?returnTo=/v/${shareCode}`, { replace: true });
          }
        }
      })
      .finally(() => setLoading(false));
  }, [shareCode, user, authLoading, navigate]);

  if (authLoading || loading) return <div className="min-h-screen grid place-items-center text-[var(--ink-soft)]">A carregar…</div>;

  if (error && !v) return (
    <div className="min-h-screen grid place-items-center px-6">
      <div className="card-surface p-10 max-w-md text-center">
        <Lock className="w-8 h-8 mx-auto text-[var(--ink-soft)]"/>
        <h1 className="font-heading text-2xl mt-3">Acesso restrito</h1>
        <p className="text-[var(--ink-soft)] mt-2 text-sm">{formatApiError(error)}</p>
        <Link to="/dashboard" className="pill-btn pill-primary mt-6 inline-flex">Ir para o painel</Link>
      </div>
    </div>
  );

  if (!v) return null;

  const isContinuous = v.service_type === "continuous";
  const remaining = Math.max((v.slots || 1) - (v.slots_filled || 0), 0);
  const isClosed = v.status === "closed";

  const onRecommend = () => {
    if (isClosed) return;
    navigate(`/recommend/${v.id}?src=share`);
  };

  return (
    <div className="min-h-screen bg-[var(--bone)] paper-noise">
      <header className="glass-nav">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[var(--forest)] text-white grid place-items-center"><Leaf className="w-5 h-5"/></div>
            <div>
              <div className="font-heading text-lg text-[var(--forest)]">CARING</div>
              <div className="eyebrow -mt-0.5">Referências</div>
            </div>
          </Link>
          <Link to="/dashboard" className="pill-btn pill-ghost !py-2 !px-3">O meu painel</Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-14">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div>
              <span className={`chip ${isContinuous ? "chip-green" : "chip-blue"}`}>
                {isContinuous ? "🟢 Serviço de Continuidade" : "🔵 Serviço Pontual"}
              </span>
              <h1 className="font-heading text-5xl mt-4 text-[var(--ink)]">{v.title}</h1>
              {isClosed && <div className="mt-4 chip chip-neutral">Fechada</div>}
            </div>
            <div className="card-surface p-6">
              <div className="grid sm:grid-cols-2 gap-3 text-sm text-[var(--ink)]">
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[var(--forest)]"/> {v.locality}</div>
                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[var(--forest)]"/> {v.schedule}</div>
                {v.hours && <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[var(--forest)]"/> {v.hours}</div>}
                {v.start_date && <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[var(--forest)]"/> Início {v.start_date}</div>}
                <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[var(--forest)]"/> {remaining} vaga{remaining !== 1 ? "s" : ""}</div>
              </div>
            </div>
            <div className="card-surface p-6">
              <div className="eyebrow">Requisitos</div>
              <p className="mt-2 text-[var(--ink)] whitespace-pre-wrap leading-relaxed">{v.requirements}</p>
            </div>
          </div>

          <aside className="space-y-4">
            {isContinuous ? (
              <div className="card-surface p-6">
                <div className="eyebrow !text-[var(--success)]">Remuneração média mensal</div>
                <div className="font-heading text-5xl text-[var(--success)] mt-1">{v.monthly_avg ? `${Number(v.monthly_avg).toFixed(0)} €` : "—"}<span className="text-lg text-[var(--ink-soft)] ml-1">/mês</span></div>
              </div>
            ) : (
              <div className="card-surface p-6">
                <div className="eyebrow !text-[var(--info)]">Valor total do serviço</div>
                <div className="font-heading text-5xl text-[var(--info)] mt-1">{v.total_value ? `${Number(v.total_value).toFixed(0)} €` : "—"}</div>
              </div>
            )}

            <div className="card-surface p-6 bg-gradient-to-br from-[var(--terracotta)] to-[#c15839] text-white border-transparent">
              <div className="eyebrow !text-white/80">O seu prémio</div>
              <div className="font-heading text-5xl mt-1">15 €</div>
              <div className="text-sm text-white/90 mt-1">em Cartão Dá por cada contratação</div>
              <button
                onClick={onRecommend}
                disabled={isClosed}
                className="mt-5 pill-btn bg-white text-[var(--terracotta)] hover:bg-[var(--muted)] w-full justify-center"
                data-testid="public-recommend-btn"
              >
                {isClosed ? "Vaga preenchida" : "Recomendar candidato"} {!isClosed && <ArrowRight className="w-4 h-4"/>}
              </button>
              {isClosed && (
                <div className="mt-3 text-xs text-white/90">
                  Esta vaga já foi preenchida e deixou de aceitar recomendações. Consulte as restantes oportunidades disponíveis.
                </div>
              )}
            </div>
            <div className="text-xs text-[var(--ink-soft)] flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-[var(--forest)] mt-0.5"/>
              Prémio pago até ao dia 8 do mês seguinte à validação. Sujeito ao protocolo.
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
